<template>
  <AppHeaderLayout>
    <div class="page">
      <header class="page-header">
        <h1>登入紀錄</h1>
      </header>

      <div class="table-card">
        <div class="search-bar">
          <label class="form-field">
            <span>搜尋</span>
            <input v-model="search.keyword" type="text" placeholder="輸入帳號、姓名或公司名稱" />
          </label>
        </div>
        <table>
          <thead>
            <tr>
              <th>帳號</th>
              <th>姓名</th>
              <th>公司名稱</th>
              <th>IP</th>
              <th>事件</th>
              <th>日期</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="record in filteredRecords" :key="record.id">
              <td>{{ record.account }}</td>
              <td>{{ record.name }}</td>
              <td>{{ record.companyName }}</td>
              <td>{{ record.ip }}</td>
              <td>
                <span class="event" :class="record.eventClass">{{ record.event }}</span>
              </td>
              <td>{{ record.date }}</td>
            </tr>
            <tr v-if="isLoading">
              <td colspan="6" class="empty">資料載入中...</td>
            </tr>
            <tr v-else-if="errorMessage">
              <td colspan="6" class="empty">{{ errorMessage }}</td>
            </tr>
            <tr v-else-if="filteredRecords.length === 0">
              <td colspan="6" class="empty">無資料</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="table-footer">
        <div class="page-size">
          <span>每頁顯示</span>
          <select v-model.number="pageSize">
            <option v-for="size in pageSizes" :key="size" :value="size">
              {{ size }}
            </option>
          </select>
          <span>筆</span>
          <span>共 {{ totalCount }} 筆</span>
        </div>

        <div class="pager">
          <button type="button" :disabled="!canPrev" @click="prevPage" aria-label="上一頁">
            &lt;
          </button>
          <span>{{ currentPage }} / {{ totalPages }}</span>
          <button type="button" :disabled="!canNext" @click="nextPage" aria-label="下一頁">
            &gt;
          </button>
        </div>
      </div>
    </div>
  </AppHeaderLayout>
</template>

<script>
import AppHeaderLayout from "@/components/AppHeaderLayout.vue";
import { GET_ALL_LOG_LOGIN } from "@/utils/requestUrl";
import { getToken } from "@/utils/sessionStoraggeUtil";

export default {
  name: "LoginRecordView",
  components: {
    AppHeaderLayout
  },
  data() {
    return {
      records: [],
      search: {
        keyword: ""
      },
      pageSize: 10,
      pageSizes: [10, 50, 100],
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      isLoading: false,
      errorMessage: ""
    };
  },
  computed: {
    filteredRecords() {
      const keyword = this.search.keyword.trim().toLowerCase();
      return this.records.filter((record) => {
        if (!keyword) {
          return true;
        }
        const account = record.account.toLowerCase();
        const name = record.name.toLowerCase();
        const companyName = (record.companyName || "").toLowerCase();
        return (
          account.includes(keyword) ||
          name.includes(keyword) ||
          companyName.includes(keyword)
        );
      });
    },
    canPrev() {
      return this.currentPage > 1;
    },
    canNext() {
      return this.currentPage < this.totalPages;
    }
  },
  watch: {
    pageSize() {
      this.currentPage = 1;
      this.fetchRecords();
    }
  },
  created() {
    this.fetchRecords();
  },
  methods: {
    getApiBase() {
      if (window._env_ && window._env_.VUE_APP_REQUEST_DOMAIN) {
        return window._env_.VUE_APP_REQUEST_DOMAIN;
      }
      return "";
    },
    getActionClass(actionName) {
      if (actionName === "登入成功") {
        return "success";
      }
      if (actionName === "登出") {
        return "logout";
      }
      if (actionName === "登入失敗") {
        return "fail";
      }
      return "";
    },
    buildHeaders() {
      const headers = {
        "Content-Type": "application/json"
      };
      const token = getToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      return headers;
    },
    async fetchRecords() {
      this.isLoading = true;
      this.errorMessage = "";
      try {
        const response = await fetch(this.getApiBase() + GET_ALL_LOG_LOGIN, {
          method: "POST",
          headers: this.buildHeaders(),
          body: JSON.stringify({
            PageIndex: this.currentPage,
            PageSize: this.pageSize
          })
        });

        let payload = null;
        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        if (!response.ok) {
          throw new Error((payload && payload.message) || "取得登入紀錄失敗");
        }

        if (payload && payload.status === "failure") {
          throw new Error(payload.message || "取得登入紀錄失敗");
        }

        const data = (payload && payload.data) || {};
        const sourceList = data.logLoginList || data.LogLoginList || [];
        const totalCount = Number(data.totalCount ?? data.TotalCount ?? 0);
        const totalPages = Number(data.totalPages ?? data.TotalPages ?? 0);

        this.records = sourceList.map((item, index) => {
          const account = item.account || item.Account || "";
          const name = item.name || item.Name || "";
          const companyName = item.companyName || item.CompanyName || "";
          const ip = item.ipAddress || item.IpAddress || "";
          const event = item.actionName || item.ActionName || "";
          const date = item.actionDate || item.ActionDate || "";

          return {
            id: `${account}-${date}-${index}`,
            account,
            name,
            companyName,
            ip,
            event,
            eventClass: this.getActionClass(event),
            date
          };
        });

        this.totalCount = totalCount;
        this.totalPages = totalPages > 0 ? totalPages : 1;

        if (this.currentPage > this.totalPages) {
          this.currentPage = this.totalPages;
          await this.fetchRecords();
        }
      } catch (error) {
        this.records = [];
        this.totalCount = 0;
        this.totalPages = 1;
        this.errorMessage = error.message || "取得登入紀錄失敗";
      } finally {
        this.isLoading = false;
      }
    },
    async prevPage() {
      if (this.canPrev) {
        this.currentPage -= 1;
        await this.fetchRecords();
      }
    },
    async nextPage() {
      if (this.canNext) {
        this.currentPage += 1;
        await this.fetchRecords();
      }
    }
  }
};
</script>

<style scoped>
.page {
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 12px 24px rgba(15, 30, 40, 0.08);
  font-size: 1rem;
}

.page-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 20px;
}

.page-header h1 {
  margin: 0;
  font-size: 1.625rem;
}

.subtitle {
  font-size: 0.875rem;
  color: rgba(31, 42, 51, 0.6);
}

.table-card {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(15, 30, 40, 0.08);
}

.search-bar {
  padding: 16px;
  border-bottom: 1px solid rgba(15, 30, 40, 0.08);
  background: #ffffff;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.9375rem;
  max-width: 320px;
}

.form-field input {
  height: 38px;
  border-radius: 10px;
  border: 1px solid rgba(20, 40, 50, 0.2);
  padding: 0 12px;
  font-size: 0.9375rem;
}

.form-field input:focus {
  outline: none;
  border-color: rgba(10, 160, 165, 0.7);
  box-shadow: 0 0 0 3px rgba(10, 160, 165, 0.15);
}

.table-card table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9375rem;
}

.table-card thead {
  background: #f4f7f8;
  text-align: left;
}

.table-card th,
.table-card td {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(15, 30, 40, 0.08);
}

.table-card tbody tr:hover {
  background: rgba(10, 160, 165, 0.06);
}

.event {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 600;
}

.event.success {
  background: rgba(16, 185, 129, 0.16);
  color: #0f8a5f;
}

.event.logout {
  background: rgba(59, 130, 246, 0.14);
  color: #1d4ed8;
}

.event.fail {
  background: rgba(239, 68, 68, 0.18);
  color: #b91c1c;
}

.empty {
  text-align: center;
  padding: 24px 0;
  color: rgba(31, 42, 51, 0.5);
}

.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 18px;
  gap: 16px;
  flex-wrap: wrap;
}

.page-size {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9375rem;
}

.page-size select {
  border-radius: 8px;
  border: 1px solid rgba(15, 30, 40, 0.2);
  padding: 4px 8px;
  font-size: 0.9375rem;
}

.pager {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9375rem;
}

.pager button {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(15, 30, 40, 0.2);
  background: #ffffff;
  cursor: pointer;
}

.pager button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

@media (max-width: 720px) {
  .page {
    padding: 20px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .table-footer {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
